import React, { useState, useEffect, useRef, useMemo } from 'react'
import { Box } from '@mui/material'
import { useAuth } from '../../../context/AuthContext'
import useNavigate from '../../hooks/useNavigate'
import { toast } from 'react-toastify'
import { editObject, getObjectsWithFilter, addObject, getObject } from '../../../api/apiClientCrud'
import { useLocation, useOutletContext } from 'react-router-dom'
import Form from '../../form/Form'
import Toolbar from '../../toolbar/Toolbar'
import { getUserFields } from './UserFields'
import { handleFileUpload } from '../../../utils/documentManager'

const ROUTES = {
  '/users/edit': 'edit',
  '/myprofile/edit': 'edit',
  '/users/view': 'view',
  '/myprofile': 'view',
  '/users/add': 'add'
}

const User = ({
  modeProps = 'normal',
  setStepFailed,
  userAdd,
  setUserAdd,
  check,
  setErrorsUser,
  setCheck,
  onValidStep
}) => {
  const [userToEdit, setUserToEdit] = useState({})
  const { user, setUser } = useAuth()
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const [file, setFile] = useState()
  const toastId = useRef(null)
  const [attach, setAttach] = useState(null)
  const location = useLocation()
  const [isFormError, setIsFormError] = useState(false)
  const [rolesList, setRolesList] = useState(null)
  const [mode, setMode] = useState(null)
  const userRight = useOutletContext()

  const prevUser = user

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        if (location.pathname.includes('/add')) return 'Nouvel Utilisateur'

        const hasName = userToEdit?.firstname || userToEdit?.lastname
        if (hasName) {
          return `Utilisateur ${userToEdit.firstname ?? ''} ${userToEdit.lastname ?? ''}`.trim()
        }
        return 'Utilisateur'
      },
      objectType: 'users',
      actions: { edit: true, back: true, audits: true, list: true }
    }),
    [userRight, location.pathname, userToEdit?.firstname, userToEdit?.lastname]
  )

  const memoizedFields = useMemo(
    () => getUserFields(modeProps, mode, attach, rolesList, user),
    [modeProps, attach, rolesList, user, mode]
  )

  const options = useMemo(
    () => ({
      tabs: [{ tabName: 'Généraux', tabIndex: '1' }],
      fields: memoizedFields,
      tabDefault: '1',
      data: userToEdit,
      location: location
    }),
    [memoizedFields, userToEdit, location]
  )

  useEffect(() => {
    if (modeProps === 'wizard') {
      setUserToEdit(userAdd)
    }
  }, [userAdd, modeProps])

  useEffect(() => {
    if (modeProps !== 'wizard') return

    setErrorsUser(isFormError)
    setStepFailed(isFormError)
  }, [isFormError, modeProps])

  const currentPathMode = ROUTES[location.pathname] || null

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await getObjectsWithFilter('roles', { company_id: user.company_id })
        setRolesList(res.data.roles)
      } catch {}
    }

    const fetchUser = async (id) => {
      try {
        const res = await getObject('users', id)
        setUserToEdit(res.data)
        if (res.data.attachments) setAttach(res.data.attachments.find((a) => a.type === 'user_logo'))
      } catch (err) {}
    }

    setMode(currentPathMode)

    if (modeProps === 'normal') {
      switch (currentPathMode) {
        case 'edit':
          if (location.state?.id) fetchUser(location.state.id)
          else setUserToEdit(user)
          fetchRoles()
          break
        case 'view':
          if (location.state?.id) fetchUser(location.state.id)
          else {
            setUserToEdit(user)
            if (user.attachments) setAttach(user.attachments.find((a) => a.type === 'user_logo'))
          }
          fetchRoles()
          break
        case 'add':
          setUserToEdit({ attachments: [] })
          fetchRoles()
          break
        default:
          break
      }
    } else {
      setUserToEdit(userAdd)
    }
  }, [location, modeProps, user, userAdd, currentPathMode])

  useEffect(() => {
    if (mode !== 'edit') return

    if (userToEdit?.file instanceof File || userToEdit?.file instanceof Blob) {
      const previewUrl = URL.createObjectURL(userToEdit.file)

      const attachToAdd = {
        ...(attach?.id ? { id: attach.id } : {}),
        type: 'user_logo',
        file: { data: previewUrl, type: 'file' }
      }

      setAttach(attachToAdd)
      setFile(userToEdit.file)

      setUserToEdit((prev) => ({
        ...prev,
        attachments: [attachToAdd]
      }))

      return () => URL.revokeObjectURL(previewUrl)
    }

    if (userToEdit?.attachments?.length > 0) {
      const existingLogo = userToEdit.attachments.find((a) => a.type === 'user_logo')
      if (existingLogo) setAttach(existingLogo)
    }
  }, [mode, userToEdit.file])

  useEffect(() => {
    if (!isSubmit) return

    const handleEdit = async () => {
      toastId.current = toast.loading('Enregistrement en cours')
      try {
        const res = await editObject('users', userToEdit.id, { ...userToEdit, lastModBy: user.email })
        if (res.status === 200) {
          if (userToEdit.id === user.id) setUser(res.data)
          setUserToEdit(res.data)

          if (file) {
            await handleFileUpload(file, user, 'user_logo', 'users', userToEdit, setUserToEdit)
              .then((uploaded) => {
                if (userToEdit.id === user.id && uploaded) {
                  setUser((prev) => ({
                    ...prev,
                    attachments: [...(prev.attachments?.filter((a) => a.type !== 'user_logo') || []), uploaded]
                  }))
                }
              })
              .catch()
          }

          toast.update(toastId.current, {
            type: 'success',
            render: 'Enregistrement terminé',
            autoClose: 2000,
            isLoading: false
          })
          history(location.state?.history || '/users/view', { state: { id: userToEdit.id } })
        }
      } catch {
        if (userToEdit.id === user.id) setUser(prevUser)
        toast.update(toastId.current, {
          type: 'error',
          render: "Erreur lors de l'enregistrement",
          autoClose: 2000,
          isLoading: false
        })
      }
    }

    const handleAdd = async () => {
      toastId.current = toast.loading('Enregistrement en cours')
      try {
        const res = await addObject('users', { ...userToEdit, company_id: user.company_id, createdBy: user.email })
        if (res.status === 201) {
          setUserToEdit(res.data)

          if (file) {
            await handleFileUpload(file, user, 'user_logo', 'users', res.data, setUserToEdit)
              .then((uploaded) => {
                if (res.data.id === user.id && uploaded) {
                  setUser((prev) => ({
                    ...prev,
                    attachments: [...(prev.attachments?.filter((a) => a.type !== 'user_logo') || []), uploaded]
                  }))
                }
              })
              .catch()
          }

          toast.update(toastId.current, {
            type: 'success',
            render: 'Enregistrement terminé',
            autoClose: 2000,
            isLoading: false
          })
          history('/users/view', { state: { id: res.data.id } })
        }
      } catch {
        toast.update(toastId.current, {
          type: 'error',
          render: "Erreur lors de l'enregistrement",
          autoClose: 2000,
          isLoading: false
        })
      }
    }

    if (modeProps === 'normal') {
      if (currentPathMode === 'edit') handleEdit()
      else if (currentPathMode === 'add') handleAdd()
    } else if (modeProps === 'wizard') {
      setUserAdd((prev) => ({
        ...prev,
        ...userToEdit
      }))
      setCheck(false)
      setErrorsUser(false)
    }

    setIsSubmit(false)
  }, [isSubmit, userToEdit, location, user, file, prevUser, setUser, setUserAdd, modeProps, history, currentPathMode])

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', overflowY: 'auto', flexGrow: 1 }}
    >
      {modeProps === 'normal' && <Toolbar options={toolbar} object={userToEdit} />}
      <Form
        modeProps={modeProps}
        checkForm={check}
        setCheckForm={setCheck}
        mode={mode}
        setFormError={setIsFormError}
        options={options}
        setDataParent={(data) => {
          setUserToEdit(data)
          if (modeProps === 'wizard') {
            setUserAdd(data)
          }
        }}
        setIsSubmitParent={setIsSubmit}
        onValidStep={onValidStep}
      />
    </Box>
  )
}

export default User
